import { StatefulIncomingCall } from '@/components/allo-stateful/dashboard/stateful-incoming-call';
import { AnnouncementIntegrationsDialog } from '@/components/announcement/announcement-integrations-dialog';
import { BusyModeBanner } from '@/components/busy-mode-banner';
import { CallEndedToastListener } from '@/components/call-ended-toast/call-ended-toast-listener';
import { Dialer } from '@/components/dashboard/dialer/dialer';
import { IntercomCustomAttributes } from '@/components/intercom/intercom-custom-attributes';
import { MicrophonePermissions } from '@/components/microphone-permissions/microphone-permissions';
import { PersonalPhoneModal } from '@/components/personal-phone-modal/personal-phone-modal';
import { Sidebar } from '@/components/sidebar/sidebar';
import { SidePanelLayout } from '@/components/sidepanel/side-panel-layout';
import { SidePanelProvider } from '@/components/sidepanel/side-panel-provider';
import { Splashscreen } from '@/components/splashscreen/splashscreen';
import { StatusPageBanner } from '@/components/status-page-banner';
import { SubscriptionWarningBanner } from '@/components/subscription-warning-banner';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { VonageUnavailableBanner } from '@/components/vonage-unavailable-banner';
import { BlockedUserScreenContainer } from '@/components/welcome/blocked-user-screen.container';
import { CheckUpDialogContainer } from '@/components/welcome/check-up-dialog.container';
import { StartWithAlloDialogContainer } from '@/components/welcome/start-with-allo-dialog.container';
import { UnassignedPhonesDialogContainer } from '@/components/welcome/unassigned-phones-dialog.container';
import {
  getLastImpersonationSnapshot,
  getStoredAdminSession,
  restoreAdminSession,
  subscribeImpersonation,
} from '@/features/impersonation-session';
import { useAmplitudeGuideTracking } from '@/hooks/amplitude/use-amplitude-guide-tracking';
import { teamQueryParams } from '@/hooks/api/team';
import { useCopyUserIdShortcut } from '@/hooks/use-copy-user-id-shortcut';
import { useSubscriptionWarning } from '@/hooks/use-subscription-warning';
import { useWebSocket } from '@/hooks/use-websocket';
import {
  AuthError,
  BlockedUserError,
  NoBusinessError,
  NoRoleError,
  UnassignedPhonesError,
  VonageError,
} from '@/lib/authentication';
import { isServerError } from '@/lib/error-classification';
import { getVonageRegion } from '@/lib/get-vonage-region';
import { getNetworkStatus } from '@/lib/network-status';
import { saveOAuthConsentReturn } from '@/lib/oauth-consent-return';
import { parseLine } from '@/lib/phone-number';
import { detectOS, isDesktop } from '@/lib/platform';
import { persistFirstName } from '@/lib/user-greeting';
import { cn } from '@/lib/utils';
import { AppCallActionsProvider } from '@/providers/app-call-actions/app-call-actions-provider';
import { AppChromeProvider, useAppChrome } from '@/providers/app-chrome/app-chrome-provider';
import { AudioDevicesProvider } from '@/providers/audio-devices/audio-devices-provider';
import { CallBarProvider } from '@/providers/call-bar/call-bar-provider';
import { CallPlatformProvider } from '@/providers/call-capabilities/call-capabilities-provider';
import { DialerProvider } from '@/providers/dialer/dialer-provider';
import { ExperimentProvider } from '@/providers/experiment';
import {
  FreshsalesProvider,
  useFreshsales,
} from '@/providers/integration-caller/freshsales-provider';
import { useHubSpot } from '@/providers/integration-caller/hubspot-provider';
import { useSalesforce } from '@/providers/integration-caller/salesforce-provider';
import { LinesProvider } from '@/providers/lines/lines-provider';
import { useLines } from '@/providers/lines/use-lines';
import { RecordingPlaybackProvider } from '@/providers/recording-playback/recording-playback-provider';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { $apiAllo } from '@/services/api-allo/client';
import { freshsalesService } from '@/services/freshsales';
import { logStructured, logger } from '@/services/logger/logger';
import { newRelicService } from '@/services/newrelic';
import { supabase } from '@/services/supabase';
import { tracingService } from '@/services/tracing/tracing-service';
import { ActivePowerDialerProvider } from '@/views/power-dialer/providers/active-power-dialer-provider';
import { PowerDialerPipBridge } from '@/views/power-dialer/providers/power-dialer-pip-bridge';
import { VoiceCallActionsProvider } from '@packages/client-sdk-react/src/voice/voice-call-actions-provider';
import { VoiceCallStateProvider } from '@packages/client-sdk-react/src/voice/voice-call-container';
import {
  VonageClientProvider,
  getVonageClient,
  getVonageUserContext,
  refreshVonageClient,
  setVonageUserContext,
  useVonageSession,
} from '@packages/client-sdk-react/src/vonage-client-provider';
import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute, redirect, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { type IntercomProps, useIntercom } from 'react-use-intercom';

// Track app start time for splash screen monitoring
declare global {
  interface Window {
    __appStartTime?: number;
  }
}

function parseVonageSdkError(message: string) {
  const lowerMsg = message.toLowerCase();

  const isFailedToFetch = lowerMsg.includes('failed to fetch');
  const isTimeout = lowerMsg.includes('timeout');
  const isNetworkError =
    isFailedToFetch || lowerMsg.includes('networkerror') || lowerMsg.includes('network error');

  let errorType: 'failed_to_fetch' | 'timeout' | 'other' = 'other';
  if (isFailedToFetch) {
    errorType = 'failed_to_fetch';
  } else if (isTimeout) {
    errorType = 'timeout';
  }

  // Extract endpoint domain from message (e.g. api-us.vonage.com)
  const endpointMatch = message.match(/(?:https?:\/\/)?(api[-\w]*\.vonage\.com)/i);
  const endpoint = endpointMatch?.[1] ?? undefined;

  // Extract region from endpoint (e.g. "us" from "api-us.vonage.com")
  const regionMatch = endpoint?.match(/api-(\w+)\.vonage\.com/);
  const vonageRegion = regionMatch?.[1] ?? undefined;

  // Extract operation from SDK topic/message context
  const operationMatch = message.match(
    /\b(serverCall|answer|reject|hangup|createSession|sendDtmf)\b/i,
  );
  const sdkOperation = operationMatch?.[1] ?? 'unknown';

  return { errorType, endpoint, sdkOperation, isNetworkError, vonageRegion };
}

export const Route = createFileRoute('/_app')({
  component: App,
  beforeLoad: async ({ context, location }) => {
    const authSess = await supabase.auth.getSession();

    if (authSess.data.session === null || authSess.error) {
      saveOAuthConsentReturn(location.href);
      throw redirect({ to: '/login', replace: true });
    }

    return context;
  },
  loader: async ({ context, location }) => {
    try {
      const { queryClient } = context as { queryClient: QueryClient };

      const userQuery = $apiAllo.queryOptions('get', '/v1/user', {});
      const userResponse = await queryClient.ensureQueryData(userQuery);
      const user = userResponse?.data;

      if (!user?.id) {
        saveOAuthConsentReturn(location.href);
        throw redirect({ to: '/login', replace: true });
      }

      if (user.status === 'BLOCKED') {
        throw new BlockedUserError();
      }

      if (!user.role) {
        throw new NoRoleError();
      }

      /**
       * @see Analytics-Sentry
       */
      Sentry.setUser({
        id: user.id,
      });

      analytics.identify(user.id);
      analytics.bootGuides();
      newRelicService.setUserId(user.id);

      // Cache the first name for the next boot's splash greeting. Best-effort
      // — localStorage failures are swallowed inside `persistFirstName`.
      persistFirstName(user.name);

      // Kick off team prefetch in parallel — non-blocking so the loader does
      // not wait for it. The sidebar members section reads from this cache
      // and renders skeleton rows until it lands.
      void queryClient.prefetchQuery($apiAllo.queryOptions(...teamQueryParams));

      const businessQuery = $apiAllo.queryOptions('get', '/v1/business', {
        params: { query: { extend: 'sender_id' } },
      });
      const businessResponse = await queryClient.ensureQueryData(businessQuery);
      const businessData = businessResponse?.data;

      const phoneNumbers = businessData?.phone_numbers;

      if (!businessData) {
        throw new NoBusinessError();
      }

      if (!phoneNumbers) {
        newRelicService.addPageAction('onboarding:app_loader:redirect_to_choose_number', {
          domain: 'onboarding',
          reason: 'no_phone_numbers_field',
          phoneCountTotal: 0,
          phoneCountWithNumber: 0,
          emptyNumberCount: 0,
          hasActive: false,
          hasUnassigned: false,
        });
        throw redirect({ to: '/choose-number', replace: true });
      }

      const numbers = phoneNumbers?.filter(
        (number) => number.number && number.number.trim() !== '',
      );

      if (numbers?.length === 0) {
        newRelicService.addPageAction('onboarding:app_loader:redirect_to_choose_number', {
          domain: 'onboarding',
          reason: 'all_empty_numbers',
          phoneCountTotal: phoneNumbers.length,
          phoneCountWithNumber: 0,
          emptyNumberCount: phoneNumbers.length,
          hasActive: false,
          hasUnassigned: false,
        });
        throw redirect({ to: '/choose-number', replace: true });
      }

      const hasUnassignedWithNumber = numbers?.some((phone) => phone.status === 'UNASSIGNED');

      const hasActiveWithNumber = numbers?.some((number) => number.status === 'ACTIVE');

      if (hasUnassignedWithNumber && !hasActiveWithNumber) {
        throw new UnassignedPhonesError();
      }

      if (!hasActiveWithNumber) {
        newRelicService.addPageAction('onboarding:app_loader:redirect_to_choose_number', {
          domain: 'onboarding',
          reason: 'no_active_with_number',
          phoneCountTotal: phoneNumbers.length,
          phoneCountWithNumber: numbers?.length ?? 0,
          emptyNumberCount: phoneNumbers.length - (numbers?.length ?? 0),
          hasActive: false,
          hasUnassigned: !!hasUnassignedWithNumber,
        });
        throw redirect({ to: '/choose-number', replace: true });
      }

      /*
       * @see Vonage-Client
       */
      const region = await getVonageRegion();

      const mapVonageLevelToNewRelic = (
        vonageLevel: string,
      ): 'ERROR' | 'TRACE' | 'DEBUG' | 'INFO' | 'WARN' => {
        const levelMap: Record<string, 'ERROR' | 'TRACE' | 'DEBUG' | 'INFO' | 'WARN'> = {
          Error: 'ERROR',
          Warn: 'WARN',
          Info: 'INFO',
          Debug: 'DEBUG',
          Verbose: 'TRACE',
        };
        return levelMap[vonageLevel] ?? 'WARN';
      };

      // Refresh the module-level user context on every loader run. The
      // customLoggers below read this lazily so they always tag events with
      // the CURRENT user, not whoever was active when the singleton was
      // first constructed. `resetVonageClient` (logout / stop-impersonation)
      // clears this same state.
      setVonageUserContext({
        userId: user.id,
        userEmail: user.email ?? null,
        userPhone: user.phone ?? null,
        businessId: businessData?.id ?? null,
        region,
      });

      // Mirror the region onto the tracing singleton so call logs can tag every
      // New Relic entry with `vonageRegion` without reaching into the Vonage
      // client provider (which would create an import cycle with the logger).
      tracingService.setVonageRegion(region);

      // Initialize the module-level VonageClient singleton. On any loader
      // re-run (router.invalidate, auth flow, impersonation) this returns the
      // already-constructed instance — we no longer mint a fresh client per
      // loader run, which was the root cause of phantom calls (see
      // specs/vonage-client-lifetime.md). The return value is ignored; every
      // other consumer reads the singleton via `getVonageClient()` without args.
      getVonageClient({
        region,
        autoReconnectMedia: true,
        loggingLevel: 'Info',
        customLoggers: [
          {
            name: 'New Relic',
            minLogLevel: 'Verbose',
            onLog: (level, topic, message) => {
              const topicName = String(topic?.name ?? 'unknown');
              const topicTag = String(topic?.tag ?? '');
              // Read the active user lazily so stale captures don't survive
              // user switches. The fallback to the closure value is a safety
              // net: if the context was unexpectedly cleared mid-flight, we
              // prefer a "slightly stale but explainable" attribute over a
              // missing one.
              const ctx = getVonageUserContext();
              logStructured(
                topicName,
                ['calls', 'vonage'],
                mapVonageLevelToNewRelic(level),
                message,
                {
                  userId: ctx?.userId ?? user.id,
                  userEmail: ctx?.userEmail ?? user.email,
                  userPhone: ctx?.userPhone ?? user.phone,
                  businessId: ctx?.businessId ?? businessData?.id,
                  region: ctx?.region ?? region,
                  topicName,
                  topicTag,
                },
              );
            },
          },
          {
            name: 'Sentry',
            minLogLevel: 'Error',
            onLog: (level, topic, message) => {
              const topicName = String(topic?.name ?? 'unknown');
              const topicTag = String(topic?.tag ?? '');
              const rawMessage = `${level} ${topicName} ${topicTag} ${message}`;
              const parsed = parseVonageSdkError(rawMessage);
              const network = getNetworkStatus();
              // See New Relic logger above. Same rationale.
              const ctx = getVonageUserContext();
              const ctxUserId = ctx?.userId ?? user.id;
              const ctxBusinessId = ctx?.businessId ?? businessData?.id;

              if (parsed.isNetworkError) {
                logger.captureException(
                  new Error(`Vonage SDK network error: ${parsed.errorType}`),
                  {
                    fingerprint: ['vonage-sdk', parsed.sdkOperation, 'network-error'],
                    tags: {
                      vonage_endpoint: parsed.endpoint ?? 'unknown',
                      vonage_operation: parsed.sdkOperation,
                      error_category: parsed.errorType,
                      network_quality: network.quality,
                      network_online: String(network.isOnline),
                      ...(parsed.vonageRegion ? { vonage_region: parsed.vonageRegion } : {}),
                      ...(region ? { configured_region: region } : {}),
                    },
                    level: 'error',
                    context: {
                      rawMessage,
                      userId: ctxUserId,
                      businessId: ctxBusinessId,
                      topicName,
                      topicTag,
                      network,
                    },
                  },
                );
              } else {
                logger.captureException(new Error(rawMessage), {
                  fingerprint: ['vonage-sdk', parsed.sdkOperation, parsed.errorType],
                  tags: {
                    vonage_operation: parsed.sdkOperation,
                    error_category: parsed.errorType,
                    ...(parsed.endpoint ? { vonage_endpoint: parsed.endpoint } : {}),
                    ...(region ? { configured_region: region } : {}),
                  },
                  level: 'error',
                  context: {
                    rawMessage,
                    userId: user.id,
                    businessId: businessData?.id,
                    topicName,
                    topicTag,
                  },
                });
              }
            },
          },
        ],
      });

      let vonageSessionError: unknown;

      // `initial-boot` is the only trigger the loader fires. `refreshVonageClient`
      // short-circuits if the singleton already has a session, so
      // `router.invalidate()` during a live call no longer rotates the Vonage
      // session. Real session refreshes happen event-driven from inside the
      // provider (EXPIRED_TOKEN handler, user-banner retry, etc.).
      // user/business/region for error telemetry come from the module user
      // context set by `setVonageUserContext` above.
      const sessionId = await refreshVonageClient({
        trigger: { type: 'initial-boot' },
      }).catch((error) => {
        if (error instanceof BlockedUserError) {
          throw error;
        }

        vonageSessionError = error;
        Sentry.captureException(error, {
          tags: {
            component: 'vonage_session_creation',
          },
          extra: {
            region,
            userId: user.id,
            businessId: businessData?.id,
          },
          level: 'warning',
        });
        return undefined;
      });

      // Track success only if session was created
      if (sessionId) {
        analytics.track(ANALYTICS_EVENTS.VOIP.REGION_CONNECTED, {
          region,
        });
      }

      return {
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          phone: user.phone,
          team_dashboard_url: user.team_dashboard_url,
          has_billing_access: user.has_billing_access,
          teamName: user.team?.name,
          role: user.role,
          // Backend returns image_url on the user endpoint but it's not yet
          // declared on the OpenAPI schema. Cast scoped to this read site.
          image_url: (user as { image_url?: string | null }).image_url ?? undefined,
        },
        voip: {
          // sessionId is read via useVonageSession() from module state.
          // `error` preserved for any retry/diagnostic surface that reads it.
          error: vonageSessionError,
        },
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'app_loader',
        },
        extra: {
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          errorMessage:
            error instanceof Error
              ? error.message
              : typeof error === 'object' && error !== null
                ? JSON.stringify(error)
                : String(error),
          isKnownError: error instanceof AuthError || error instanceof VonageError,
          isRedirect: !!(error as any)?.isRedirect,
        },
      });
      throw error;
    }
  },
  pendingMs: 1000,
  pendingComponent: () => {
    // Track splash screen appearances for monitoring
    const appStartTime = window.__appStartTime ?? Date.now();
    const timeSinceAppStart = Date.now() - appStartTime;
    const isInitialLoad = timeSinceAppStart < 5000; // First 5 seconds
    const platform = isDesktop() ? 'electron' : 'web';
    const os = detectOS();

    // Track all splash appearances to monitor behavior
    analytics.track('Splash Screen Appeared', {
      timeSinceAppStart,
      timeSinceAppStartSeconds: Math.round(timeSinceAppStart / 1000),
      isInitialLoad,
      currentPath: window.location.pathname,
      previousPath: document.referrer || 'direct',
      platform, // 'electron' or 'web'
      os, // 'windows', 'mac', or 'unknown'
    });

    return (
      <div className="size-full animate-in fade-in duration-1000">
        <Splashscreen />
      </div>
    );
  },
  errorComponent: ({ error }) => {
    if (error instanceof BlockedUserError) {
      return <BlockedUserScreenContainer />;
    }

    if (error instanceof UnassignedPhonesError) {
      return <UnassignedPhonesDialogContainer />;
    }

    // AuthError variants render the splash in non-error mode (loading shape)
    // while the auth flow resolves; keep that behavior.
    if (error instanceof AuthError) {
      return (
        <div className="size-full">
          {error instanceof NoRoleError && <StartWithAlloDialogContainer />}
          <Splashscreen noTimeout={true} />
        </div>
      );
    }

    // Backend-reachability signature → server framing (status-page CTA lands
    // here). Everything else (render bugs, unknown throws from inside the
    // loader) → client framing so we never claim "we can't reach our
    // servers" on a JS bug.
    return (
      <div className="size-full">
        <Splashscreen
          error={error}
          kind={isServerError(error) ? 'server' : 'client'}
          noTimeout={true}
        />
      </div>
    );
  },
  staleTime: 1000 * 60 * 60 * 2, // 2 hours
});

const WebSocketConnector = ({
  userId,
  selectedConversationNumber,
}: { userId: string; selectedConversationNumber?: string }) => {
  const { currentLine } = useLines();
  const alloNumber = currentLine.phoneNumber.number.number;
  useWebSocket(userId, selectedConversationNumber, alloNumber);
  return null;
};

function App() {
  const { update } = useIntercom();
  const { user } = Route.useLoaderData();
  const { isReady: isHubSpotEnvironment } = useHubSpot();
  const { isReady: isSalesforceEnvironment } = useSalesforce();

  const {
    isInitialized: isFreshsalesEnvironment,
    setIsInitialized,
    setCurrentOutboundEvent,
  } = useFreshsales();

  const isCtiEnvironment =
    isHubSpotEnvironment || isFreshsalesEnvironment || isSalesforceEnvironment;

  // Hidden keyboard shortcut: Cmd+Shift+U to copy user ID
  useCopyUserIdShortcut();

  // Track Amplitude guide interactions
  useAmplitudeGuideTracking();

  const { number: selectedConversationNumber } = useSearch({ strict: false }) as {
    number?: string;
  };

  const { data: businessResponse, isLoading: isBusinessLoading } = $apiAllo.useQuery(
    'get',
    '/v1/business',
    { params: { query: { extend: 'sender_id' } } },
  );

  const business = businessResponse?.data;

  const lines = useMemo(
    () =>
      business?.phone_numbers
        ?.filter((phoneNumber) => {
          // Include both REGULAR lines (with number) and SENDER_ID lines
          if (phoneNumber.type === 'SENDER_ID') {
            return true;
          }
          return phoneNumber.number;
        })
        .map((phoneNumber) =>
          parseLine({
            number: phoneNumber.number,
            name: phoneNumber.name,
            isShared: phoneNumber.is_shared_number,
            isOutboundSMSEnabled: phoneNumber.is_outbound_sms_enabled ?? false,
            sms10DLCStatus: phoneNumber.sms_10dlc_status ?? null,
            isOutboundMMSEnabled: phoneNumber.is_outbound_mms_enabled ?? false,
            mode: phoneNumber.mode,
            type: phoneNumber.type ?? 'REGULAR',
            senderId: phoneNumber.sender_id ?? undefined,
            canMakeCalls: phoneNumber.can_make_calls,
            country: phoneNumber.country,
            rateGroup: phoneNumber.rate_group,
            status: phoneNumber.status,
            defaultSummaryTemplateId: phoneNumber.default_summary_template_id,
          }),
        )
        .filter((line) => line !== undefined) ?? [],
    [business?.phone_numbers],
  );

  /**
   * @see Analytics-Intercom
   */
  useEffect(() => {
    const updatePayload: Partial<IntercomProps> = {
      userId: user.id,
    };

    if (business?.email) {
      updatePayload.email = business.email;
    }

    if (user.phone) {
      updatePayload.phone = user.phone;
    }

    if (business?.id && business?.name) {
      updatePayload.company = {
        companyId: business.id,
        name: business.name,
      };
    }

    update(updatePayload);
  }, [update, user.id, user.phone, business?.id, business?.name, business?.email]);

  /**
   * @see Iframe Message Listener - Listen for events from parent window
   */
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const { source, type, data } = event.data ?? {};

      if (source !== 'freshworks-cti') {
        return;
      }

      console.debug('[App] Freshworks CTI message received:', {
        type,
        data,
        source,
      });

      switch (type) {
        case 'app-initialized':
          setIsInitialized(true);
          break;

        case 'call-triggered':
          if (data?.phoneNumber) {
            setIsInitialized(true);
            setCurrentOutboundEvent({ phoneNumber: data.phoneNumber });
            freshsalesService
              .startOutboundCall(data.phoneNumber, data.contactId)
              .catch(console.warn);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [setIsInitialized, setCurrentOutboundEvent]);

  if (isBusinessLoading) {
    return null;
  }

  return (
    <ExperimentProvider userId={user.id}>
      <VonageClientProvider>
        <VoiceCallStateProvider enableLocalCache>
          <VoiceCallActionsProvider>
            <LinesProvider teamName={user.teamName ?? ''} lines={lines}>
              <WebSocketConnector
                userId={user.id}
                selectedConversationNumber={selectedConversationNumber}
              />
              <AudioDevicesProvider>
                <IntercomCustomAttributes />
                <AppCallActionsProvider>
                  <CallPlatformProvider>
                    <RecordingPlaybackProvider>
                      <CallBarProvider>
                        <DialerProvider>
                          <FreshsalesProvider>
                            <AppLayout isCtiEnvironment={isCtiEnvironment} />

                            {isCtiEnvironment && <Dialer />}

                            <StatefulIncomingCall
                              variant={isCtiEnvironment ? 'integrations' : 'default'}
                            />
                          </FreshsalesProvider>
                        </DialerProvider>
                      </CallBarProvider>
                    </RecordingPlaybackProvider>
                  </CallPlatformProvider>
                </AppCallActionsProvider>
              </AudioDevicesProvider>
            </LinesProvider>
          </VoiceCallActionsProvider>
        </VoiceCallStateProvider>
      </VonageClientProvider>
    </ExperimentProvider>
  );
}

function AppLayout({
  isCtiEnvironment,
}: {
  isCtiEnvironment: boolean;
}) {
  const { showWarning, warningType } = useSubscriptionWarning();
  const { isAvailable: isVonageAvailable } = useVonageSession();

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden animate-in fade-in duration-100">
      <ImpersonationBanner />
      {showWarning && warningType && !isCtiEnvironment && (
        <SubscriptionWarningBanner type={warningType} />
      )}
      <MicrophonePermissions isIntegrations={isCtiEnvironment} />
      <BusyModeBanner isIntegrations={isCtiEnvironment} />
      {!isVonageAvailable && <VonageUnavailableBanner />}
      <StatusPageBanner />
      {!isCtiEnvironment && <CheckUpDialogContainer />}
      {!isCtiEnvironment && <PersonalPhoneModal />}
      {!isCtiEnvironment && <AnnouncementIntegrationsDialog />}
      {!isCtiEnvironment && (
        <AppChromeProvider>
          <ActivePowerDialerProvider>
            <PowerDialerPipBridge />
            <CallEndedToastListener />
            <SidebarProvider
              name="main-sidebar"
              className="min-h-0 flex-1"
              style={
                {
                  '--sidebar-width': '220px',
                  '--sidebar-top-offset': showWarning ? '36px' : '0px',
                } as React.CSSProperties
              }
            >
              <SidePanelProvider>
                <CollapsibleAppSidebar />
                <CollapsibleSidebarInset>
                  <SidePanelLayout>
                    <Outlet />
                  </SidePanelLayout>
                </CollapsibleSidebarInset>
              </SidePanelProvider>
            </SidebarProvider>
          </ActivePowerDialerProvider>
        </AppChromeProvider>
      )}
    </div>
  );
}

/**
 * Wraps the app sidebar in a width-animated container so it can slide off-screen
 * when full-attention surfaces (e.g. Power Dialer focus mode) request it via
 * `AppChromeProvider`.
 */
function CollapsibleAppSidebar() {
  const { hideSidebar } = useAppChrome();
  return (
    <div
      className={cn(
        // bg-primary-3 here (mirroring the sidebar's own bg) keeps a clean
        // backdrop under the content while it fades out — without it, the
        // wrapper is transparent and the chrome's gray would bleed through.
        'flex h-full overflow-hidden bg-primary-3 transition-[width] duration-500',
        hideSidebar ? 'w-0' : 'w-(--sidebar-width)',
      )}
      style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.0, 1)' }}
    >
      {/* Inner stays at natural width while the wrapper clips; opacity + a
       *  small leftward translate fade in lockstep with the width transition
       *  (same 500ms duration + easing) so the content dissolves *with* the
       *  box and follows the same direction of motion rather than getting
       *  guillotined in place. */}
      <div
        data-state={hideSidebar ? 'hidden' : 'visible'}
        className={cn(
          // `translate` in Tailwind v4 uses the modern `translate` CSS property,
          // not `transform` — so the transition has to list it explicitly,
          // otherwise the translate snaps instead of animating.
          'h-full w-(--sidebar-width) [transition-property:opacity,translate] duration-500',
          hideSidebar ? 'opacity-0 -translate-x-2' : 'opacity-100 translate-x-0',
        )}
        style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.0, 1)' }}
      >
        <Sidebar />
      </div>
    </div>
  );
}

/**
 * Adds left padding to the main content area when the sidebar is hidden,
 * so the bottom dialer and columns aren't flush against the window edge.
 */
function CollapsibleSidebarInset({ children }: { children: React.ReactNode }) {
  const { hideSidebar } = useAppChrome();
  return (
    <SidebarInset
      className={cn(
        'overflow-hidden transition-[padding] duration-500 ease-[cubic-bezier(0.4,0,0.0,1)]',
        hideSidebar && 'pl-2',
      )}
    >
      {children}
    </SidebarInset>
  );
}

function ImpersonationBanner() {
  const { user } = Route.useLoaderData();

  const lastState = useSyncExternalStore(subscribeImpersonation, getLastImpersonationSnapshot);
  const state = getStoredAdminSession();

  if (!lastState?.impersonatedBy) {
    return null;
  }

  const adminLabel = state?.adminName || state?.adminEmail || 'admin';

  return (
    <div className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-100 px-4 py-2 text-body-14 text-amber-950">
      <span className="truncate">
        You are impersonating {user.email ?? lastState.targetEmail ?? 'this user'} as {adminLabel}
      </span>
      <button
        type="button"
        className="shrink-0 rounded-sm border border-amber-500 px-3 py-1 font-medium hover:bg-amber-200"
        onClick={async () => {
          const restored = await restoreAdminSession();
          // Hard reload destroys the VonageClient module singleton cleanly,
          // matching start-impersonation (src/routes/_app/internal/users.tsx).
          // An SPA-level resetVonageClient + router.invalidate crashes because
          // VonageClientProvider re-renders between the reset and the loader
          // re-run — getVonageClient() without a config throws when the
          // singleton is null.
          window.location.href = restored ? '/' : '/login';
        }}
      >
        Stop impersonating
      </button>
    </div>
  );
}
